import { SVGProps } from "react";
import DashboardIcon from "../../../assets/svg/SideNav/DashboardIcon";
import SubmittedTestOrdersIcon from "../../../assets/svg/SideNav/SubmittedTestOrdersIcon";
import PatientsIcon from "../../../assets/svg/SideNav/PatientsIcon";
import PDFIcon from "../../../assets/svg/SideNav/PDFIcon";
import SettingsIcon from "../../../assets/svg/SideNav/SettingsIcon";
import ShipmentsIcon from "../../../assets/svg/SideNav/ShipmentsIcon";
import ApprovalsIcon from "../../../assets/svg/SideNav/ApprovalsIcon";
import NotificationsSettingsIcon from "../../../assets/svg/SideNav/NotificationsSettingsIcon";
import helpIcon from "../../../assets/svg/SideNav/help";

export type FeatureFlagKey =
  | "DASHBOARD"
  | "TEST_ORDERS"
  | "APPROVALS"
  | "PATIENTS"
  | "DOCUMENTS"
  | "SETTINGS"
  | "NOTIFICATIONS"
  | "HELP"
  | "USER_PROFILE"
  | "SHIPMENTS";

export type IconComponent = (props: SVGProps<SVGSVGElement>) => JSX.Element;

export interface NavigationLinkConfig {
  id: string;
  title: string;
  description: string;
  route: string;
  icon: IconComponent;
  keywords: string[];
  speechPhrases?: string[];
  featureFlagKey?: FeatureFlagKey;
  section?: "navigation" | "settings";
}

const navigationLinks: NavigationLinkConfig[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    description: "Monitor orders, alerts, and shortcuts from one place.",
    route: "/access/dashboard",
    icon: DashboardIcon,
    keywords: ["dashboard", "home", "overview", "metrics"],
    speechPhrases: ["go to dashboard", "open dashboard"],
    featureFlagKey: "DASHBOARD",
  },
  {
    id: "test-orders",
    title: "Test Orders",
    description: "Search or create lab test orders for your patients.",
    route: "/access/test-orders",
    icon: SubmittedTestOrdersIcon,
    keywords: ["test", "orders", "tests", "online order", "order"],
    speechPhrases: ["create test order", "open test orders"],
    featureFlagKey: "TEST_ORDERS",
  },
  {
    id: "patients",
    title: "Patients",
    description: "Review patient records, demographics, and history.",
    route: "/access/patients",
    icon: PatientsIcon,
    keywords: ["patient", "patients", "people", "records"],
    speechPhrases: ["find patients", "patient list"],
    featureFlagKey: "PATIENTS",
  },
  {
    id: "documents",
    title: "Documents",
    description: "Manage uploaded reports, requisitions, and paperwork.",
    route: "/access/documents",
    icon: PDFIcon,
    keywords: ["documents", "files", "reports", "pdfs"],
    speechPhrases: ["documents tab", "open documents"],
    featureFlagKey: "DOCUMENTS",
  },
  {
    id: "shipments",
    title: "Shipments",
    description: "Schedule pickups and track specimen shipments.",
    route: "/access/shipments",
    icon: ShipmentsIcon,
    keywords: ["shipments", "shipping", "pickup", "logistics"],
    speechPhrases: ["schedule pickup", "shipment status"],
    featureFlagKey: "SHIPMENTS",
  },
  {
    id: "approvals",
    title: "Approvals",
    description: "Complete pending approvals and monitor requests.",
    route: "/access/approvals",
    icon: ApprovalsIcon,
    keywords: ["approvals", "review", "sign off", "authorize"],
    speechPhrases: ["approval queue", "approve order"],
    featureFlagKey: "APPROVALS",
  },
  {
    id: "notifications",
    title: "Notifications",
    description: "Review recent alerts and adjust notification settings.",
    route: "/access/notifications",
    icon: NotificationsSettingsIcon,
    keywords: ["notifications", "alerts", "messages"],
    speechPhrases: ["show notifications", "notification center"],
    featureFlagKey: "NOTIFICATIONS",
  },
  {
    id: "help",
    title: "Help Center",
    description: "Browse FAQs, videos, and support documentation.",
    route: "/access/help",
    icon: helpIcon,
    keywords: ["help", "support", "faq", "guide"],
    speechPhrases: ["open help", "support center"],
    featureFlagKey: "HELP",
  },
  {
    id: "settings",
    title: "Settings",
    description: "Manage organizations, users, and portal preferences.",
    route: "/access/settings",
    icon: SettingsIcon,
    keywords: ["settings", "preferences", "configuration"],
    speechPhrases: ["open settings", "portal settings"],
    featureFlagKey: "SETTINGS",
    section: "settings",
  },
  {
    id: "users",
    title: "Portal Users",
    description: "Invite, edit, or deactivate users in your organization.",
    route: "/access/settings/users",
    icon: SettingsIcon,
    keywords: ["users", "team", "staff", "portal users"],
    speechPhrases: ["manage users", "open users"],
    featureFlagKey: "SETTINGS",
    section: "settings",
  },
];

export default navigationLinks;

